import { Button, Form, Select, Tooltip } from "antd";
import { ThunderboltOutlined } from "@ant-design/icons";
import { useLessonForm } from "./LessonFormContext";
import {
  ActivityMainDevelopmentDomain,
  ActivityInteractionType,
  ActivityTypeOfEducationalActivity,
  ActivityBloomLevel,
  ActivityPrimariaInteractionType,
  ActivityEvidenceType,
  ActivityNature,
  resolveActivityMainDevelopmentDomain,
  resolveActivityInteractionType,
  resolveActivityTypeOfEducationalActivity,
  resolveActivityBloomLevel,
  resolveActivityPrimariaInteractionType,
  resolveActivityEvidenceType,
  resolveActivityNature,
} from "../utils";
import CustomSelect from "../../../common/custom-select";

interface PedagogicalPillsProps {
  activityIndex: number;
}

const PedagogicalPills: React.FC<PedagogicalPillsProps> = ({
  activityIndex,
}) => {
  const {
    lesson,
    educationLevel,
    pillSuggestions,
    setPillSuggestions,
    pillLoading,
    handleMainDevelopmentDomains,
    handleInteractionTypes,
    handleTypesOfEducationalActivities,
    handleSuggestPills,
  } = useLessonForm();

  const pills = pillSuggestions?.[activityIndex] || {};
  const activity = lesson.activities[activityIndex];
  const isPrimaria = educationLevel === "PRIMARIA";

  const handlePillChange = (field: string, value: any) => {
    setPillSuggestions((prev) => ({
      ...prev,
      [activityIndex]: { ...prev[activityIndex], [field]: value },
    }));
  };

  const IAButton = (
    <Tooltip title="Generar sugerencias con IA">
      <Button
        type="primary"
        icon={<ThunderboltOutlined />}
        loading={pillLoading[activityIndex]}
        onClick={() => handleSuggestPills(activityIndex)}
        style={{ minWidth: "40px" }}
      >
        {pillLoading[activityIndex] ? "" : "IA"}
      </Button>
    </Tooltip>
  );

  if (isPrimaria) {
    const currentNaturaleza = (pills.activity_nature || []) as ActivityNature[];
    const iaCombinada = currentNaturaleza.length === 2;

    return (
      <div
        className="activity-pills-row"
        style={{
          display: "flex",
          gap: 16,
          alignItems: "flex-start",
          marginBottom: 16,
        }}
      >
        <div style={{ flex: 1 }}>
          <Form.Item label="Nivel cognitivo (Bloom)" required>
            <Select
              value={
                (pills.bloom_level?.[0] || undefined) as
                  | ActivityBloomLevel
                  | undefined
              }
              placeholder="Selecciona nivel Bloom"
              onChange={(v: ActivityBloomLevel) =>
                handlePillChange("bloom_level", [v])
              }
              options={Object.values(ActivityBloomLevel).map((v) => ({
                value: v,
                label: resolveActivityBloomLevel(v),
              }))}
            />
          </Form.Item>
        </div>
        <div style={{ flex: 1 }}>
          <Form.Item label="Tipo de interacción" required>
            <Select
              value={
                (pills.primaria_interaction_type?.[0] || undefined) as
                  | ActivityPrimariaInteractionType
                  | undefined
              }
              placeholder="Selecciona tipo de interacción"
              onChange={(v: ActivityPrimariaInteractionType) =>
                handlePillChange("primaria_interaction_type", [v])
              }
              options={Object.values(ActivityPrimariaInteractionType).map(
                (v) => ({
                  value: v,
                  label: resolveActivityPrimariaInteractionType(v),
                }),
              )}
            />
          </Form.Item>
        </div>
        <div style={{ flex: 1 }}>
          <Form.Item label="Tipo de evidencia observable" required>
            <Select
              mode="multiple"
              maxTagCount={2}
              maxTagTextLength={10}
              value={(pills.evidence_type || []) as ActivityEvidenceType[]}
              placeholder="Selecciona tipo de evidencia"
              onChange={(v: ActivityEvidenceType[]) =>
                handlePillChange("evidence_type", v.slice(0, 2))
              }
              options={Object.values(ActivityEvidenceType).map((v) => ({
                value: v,
                label: resolveActivityEvidenceType(v),
              }))}
            />
          </Form.Item>
        </div>
        <div style={{ flex: 1 }}>
          <Form.Item
            label={
              iaCombinada
                ? "Naturaleza combinada (sugerido por IA)"
                : "Naturaleza de la actividad"
            }
            required
          >
            <Select
              mode="multiple"
              value={currentNaturaleza}
              placeholder="Selecciona naturaleza"
              onChange={(v: ActivityNature[]) => {
                // Permitir máximo 2 opciones
                if (v.length > 2) return;
                // Si hay 2, solo se puede quitar
                if (currentNaturaleza.length === 2 && v.length >= 2) return;
                handlePillChange("activity_nature", v);
              }}
              options={Object.values(ActivityNature).map((v) => ({
                value: v,
                label: resolveActivityNature(v),
              }))}
              maxTagCount={2}
              maxTagTextLength={12}
            />
          </Form.Item>
        </div>
        <div style={{ flexShrink: 0 }}>
          <Form.Item label=" ">{IAButton}</Form.Item>
        </div>
      </div>
    );
  }

  // Preescolar: usar pillSuggestions para autocompletar
  const mainDevelopmentDomains =
    pills.main_development_domain || activity.mainDevelopmentDomains || [];
  const interactionTypes =
    pills.interaction_type || activity.interactionTypes || [];
  const typesOfEducationalActivities =
    pills.type_of_educational_activity ||
    activity.typesOfEducationalActivities ||
    [];

  return (
    <div
      className="activity-pills-row"
      style={{
        display: "flex",
        gap: 16,
        alignItems: "flex-start",
        marginBottom: 16,
      }}
    >
      <div style={{ flex: 1 }}>
        <CustomSelect
          label="Dominio de desarrollo principal"
          placeholder="Selecciona un dominio de desarrollo"
          options={Object.values(ActivityMainDevelopmentDomain).map((d) => ({
            value: d,
            label: resolveActivityMainDevelopmentDomain(d),
          }))}
          value={mainDevelopmentDomains}
          onChange={(value: ActivityMainDevelopmentDomain[]) => {
            handleMainDevelopmentDomains(activityIndex, value);
            handlePillChange("main_development_domain", value);
          }}
          required
          className="activity-development-domain"
          validateStatus={
            mainDevelopmentDomains.length > 0 ? "success" : "error"
          }
          help={
            mainDevelopmentDomains.length > 0
              ? null
              : "El dominio de desarrollo es obligatorio"
          }
        />
      </div>
      <div style={{ flex: 1 }}>
        <CustomSelect
          label="Tipo de interacción"
          placeholder="Selecciona un tipo de interacción"
          options={Object.values(ActivityInteractionType).map((t) => ({
            value: t,
            label: resolveActivityInteractionType(t),
          }))}
          value={interactionTypes}
          onChange={(value: ActivityInteractionType[]) => {
            handleInteractionTypes(activityIndex, value);
            handlePillChange("interaction_type", value);
          }}
          required
          className="activity-interaction-type"
          validateStatus={interactionTypes.length > 0 ? "success" : "error"}
          help={
            interactionTypes.length > 0
              ? null
              : "El tipo de interacción es obligatorio"
          }
        />
      </div>
      <div style={{ flex: 1 }}>
        <CustomSelect
          label="Tipo de actividad didáctica preescolar"
          placeholder="Selecciona un tipo de actividad"
          options={Object.values(ActivityTypeOfEducationalActivity).map(
            (t) => ({
              value: t,
              label: resolveActivityTypeOfEducationalActivity(t),
            }),
          )}
          value={typesOfEducationalActivities}
          onChange={(value: ActivityTypeOfEducationalActivity[]) => {
            handleTypesOfEducationalActivities(activityIndex, value);
            handlePillChange("type_of_educational_activity", value);
          }}
          required
          className="activity-type-of-educational-activity"
          validateStatus={
            typesOfEducationalActivities.length > 0 ? "success" : "error"
          }
          help={
            typesOfEducationalActivities.length > 0
              ? null
              : "El tipo de actividad es obligatorio"
          }
        />
      </div>
      <div style={{ flexShrink: 0 }}>
        <Form.Item label=" ">{IAButton}</Form.Item>
      </div>
    </div>
  );
};

export default PedagogicalPills;
